import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CheckIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  createAssessment,
  publishAssessment,
  createLink,
  addQuestions,
  getQuestions,
} from '../../api/assessmentApi';
import { listQuestions, getCategories } from '../../api/questionApi';
import { listBootcamps } from '../../api/bootcampApi';
import Spinner from '../../components/ui/Spinner';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';

/* ── Yup schema for step 1 ─────────────────────────────────────── */
const detailsSchema = yup.object({
  title:               yup.string().required('Title is required').min(2, 'Too short'),
  description:         yup.string().nullable(),
  bootcamp_id:         yup.string().nullable().optional(),
  time_limit_minutes:  yup.number().nullable().min(1).transform((v) => (isNaN(v) ? null : v)),
  max_attempts:        yup.number().nullable().min(1).transform((v) => (isNaN(v) ? null : v)),
  is_anonymous:        yup.boolean(),
});

/* ── Step indicator ─────────────────────────────────────────────── */
const STEPS = ['Details', 'Questions', 'Share'];

function StepIndicator({ current }) {
  return (
    <nav aria-label="Progress" className="flex items-center justify-center gap-0">
      {STEPS.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div
                className={clsx(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                  done  && 'border-primary-600 bg-primary-600 text-white',
                  active && 'border-primary-600 bg-white text-primary-600',
                  !done && !active && 'border-neutral-300 bg-white text-neutral-400'
                )}
              >
                {done ? <CheckIcon className="h-4 w-4" /> : step}
              </div>
              <span
                className={clsx(
                  'mt-1.5 text-xs font-medium',
                  active ? 'text-primary-600' : done ? 'text-neutral-600' : 'text-neutral-400'
                )}
              >
                Step {step} {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={clsx(
                  'mx-3 mb-5 h-0.5 w-16 sm:w-24 transition-colors',
                  done ? 'bg-primary-600' : 'bg-neutral-200'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/* ── Step 1 – Details ───────────────────────────────────────────── */
function Step1Details({ onCreated }) {
  const [saving, setSaving] = useState(false);
  const [bootcamps, setBootcamps] = useState([]);

  useEffect(() => {
    listBootcamps({ per_page: 100 })
      .then((res) => {
        const d = res.data;
        setBootcamps(d.data ?? d ?? []);
      })
      .catch(() => {}); // non-critical — dropdown just stays empty
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(detailsSchema),
    defaultValues: { is_anonymous: false },
  });

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (!payload.time_limit_minutes) delete payload.time_limit_minutes;
      if (!payload.max_attempts) delete payload.max_attempts;
      const bid = parseInt(payload.bootcamp_id, 10);
      if (!bid || isNaN(bid)) delete payload.bootcamp_id;
      else payload.bootcamp_id = bid;
      const res = await createAssessment(payload);
      const assessment = res.data?.data ?? res.data;
      toast.success('Assessment created!');
      onCreated(assessment);
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to create assessment';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Title */}
      <div>
        <label className="label-base" htmlFor="title">
          Title <span className="text-error-500">*</span>
        </label>
        <input
          id="title"
          {...register('title')}
          className={clsx('input-base', errors.title && 'border-error-400 focus:border-error-500 focus:ring-error-500')}
          placeholder="e.g. Founder Readiness Assessment"
        />
        {errors.title && <p className="mt-1 text-xs text-error-600">{errors.title.message}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="label-base" htmlFor="description">Description</label>
        <textarea
          id="description"
          rows={3}
          {...register('description')}
          className="input-base resize-none"
          placeholder="Optional description…"
        />
      </div>

      {/* Bootcamp */}
      <div>
        <label className="label-base" htmlFor="bootcamp_id">Bootcamp (optional)</label>
        <select
          id="bootcamp_id"
          {...register('bootcamp_id')}
          className="input-base"
        >
          <option value="">— No bootcamp —</option>
          {bootcamps.map((b) => (
            <option key={b.bootcamp_id ?? b.id} value={b.bootcamp_id ?? b.id}>
              {b.title}{b.cohort_name ? ` — ${b.cohort_name}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Time limit */}
        <div>
          <label className="label-base" htmlFor="time_limit_minutes">Time Limit (minutes)</label>
          <input
            id="time_limit_minutes"
            type="number"
            min={1}
            {...register('time_limit_minutes')}
            className={clsx('input-base', errors.time_limit_minutes && 'border-error-400')}
            placeholder="No limit"
          />
          {errors.time_limit_minutes && (
            <p className="mt-1 text-xs text-error-600">{errors.time_limit_minutes.message}</p>
          )}
        </div>

        {/* Max attempts */}
        <div>
          <label className="label-base" htmlFor="max_attempts">Max Attempts</label>
          <input
            id="max_attempts"
            type="number"
            min={1}
            {...register('max_attempts')}
            className={clsx('input-base', errors.max_attempts && 'border-error-400')}
            placeholder="Unlimited"
          />
          {errors.max_attempts && (
            <p className="mt-1 text-xs text-error-600">{errors.max_attempts.message}</p>
          )}
        </div>
      </div>

      {/* Anonymous toggle */}
      <div className="flex items-center gap-3">
        {/* checkbox styled as toggle — see input below */}
        <label htmlFor="is_anonymous" className="flex items-center gap-3 cursor-pointer select-none">
          <input
            id="is_anonymous"
            type="checkbox"
            {...register('is_anonymous')}
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-neutral-700">Anonymous responses</span>
        </label>
        <span className="text-xs text-neutral-400">Participant identities will not be recorded</span>
      </div>

      <div className="flex justify-end pt-2">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving && <Spinner size="sm" />}
          Create &amp; Continue →
        </button>
      </div>
    </form>
  );
}

/* ── Step 2 – Questions ─────────────────────────────────────────── */
function Step2Questions({ assessment, onContinue, onSkip }) {
  const [library, setLibrary] = useState([]);
  const [selected, setSelected] = useState([]);
  const [added, setAdded] = useState([]);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loadingLib, setLoadingLib] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCategories()
      .then((res) => setCategories(res.data?.data ?? res.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingLib(true);
    const params = {};
    if (search) params.search = search;
    if (categoryFilter) params.category_id = categoryFilter;
    listQuestions({ ...params, per_page: 50 })
      .then((res) => setLibrary(res.data?.data ?? res.data ?? []))
      .catch(() => toast.error('Failed to load questions'))
      .finally(() => setLoadingLib(false));
  }, [search, categoryFilter]);

  // Load already-added questions
  useEffect(() => {
    getQuestions(assessment.id)
      .then((res) => setAdded(res.data?.data ?? res.data ?? []))
      .catch(() => {});
  }, [assessment.id]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAdd = async () => {
    if (!selected.length) { toast.error('Select at least one question'); return; }
    setSaving(true);
    try {
      await addQuestions(assessment.id, { questions: selected.map((qid) => ({ question_id: qid })) });
      toast.success(`${selected.length} question(s) added`);
      setSelected([]);
      const res = await getQuestions(assessment.id);
      setAdded(res.data?.data ?? res.data ?? []);
    } catch {
      toast.error('Failed to add questions');
    } finally {
      setSaving(false);
    }
  };

  const addedIds = new Set(added.map((q) => q.question_id ?? q.id));

  return (
    <div className="space-y-6">
      {/* Current questions */}
      {added.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-neutral-700 mb-2">
            Added Questions ({added.length})
          </h3>
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
            {added.map((q, idx) => (
              <li key={q.question_id ?? q.id} className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                  {idx + 1}
                </span>
                <span className="flex-1 truncate">{q.text ?? q.question_text ?? q.content}</span>
                {q.is_required && <Badge variant="error">Required</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Library */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Question Library</h3>
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <input
            type="text"
            placeholder="Search questions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base text-sm flex-1"
          />
          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-base text-sm w-full sm:w-44"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
        {loadingLib ? (
          <div className="flex justify-center py-8">
            <Spinner size="md" className="text-primary-600" />
          </div>
        ) : library.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No questions found.</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
            {library.map((q) => {
              const qid = q.question_id ?? q.id;
              const alreadyAdded = addedIds.has(qid);
              return (
                <li
                  key={qid}
                  className={clsx(
                    'flex items-start gap-3 px-4 py-3 transition-colors',
                    alreadyAdded ? 'bg-neutral-50 opacity-60' : 'hover:bg-neutral-50'
                  )}
                >
                  <input
                    type="checkbox"
                    id={`lib-${qid}`}
                    checked={selected.includes(qid)}
                    disabled={alreadyAdded}
                    onChange={() => toggle(qid)}
                    className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`lib-${qid}`}
                    className={clsx('flex-1 text-sm', alreadyAdded ? 'cursor-not-allowed' : 'cursor-pointer')}
                  >
                    <span className="block text-neutral-900">{q.text ?? q.question_text ?? q.content}</span>
                    <span className="text-xs text-neutral-400 mt-0.5 block capitalize">
                      {q.question_type?.replace('_', ' ')}
                      {alreadyAdded && ' · Already added'}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={handleAdd}
            disabled={saving || !selected.length}
          >
            {saving && <Spinner size="sm" />}
            Add Selected ({selected.length})
          </button>
        </div>
      </div>

      <div className="flex justify-between pt-2 border-t border-neutral-100">
        <button type="button" className="btn-ghost text-sm" onClick={onSkip}>
          Skip for now
        </button>
        <button type="button" className="btn-primary text-sm" onClick={onContinue}>
          Continue →
        </button>
      </div>
    </div>
  );
}

/* ── Step 3 – Share ─────────────────────────────────────────────── */
function Step3Share({ assessment }) {
  const [published, setPublished] = useState(assessment.status === 'published');
  const [link, setLink] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishAssessment(assessment.id);
      setPublished(true);
      toast.success('Assessment published!');
    } catch {
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    try {
      const res = await createLink(assessment.id, {});
      const lk = res.data?.data ?? res.data;
      setLink(lk);
      toast.success('Share link created!');
    } catch {
      toast.error('Failed to create link');
    } finally {
      setCreatingLink(false);
    }
  };

  const linkUrl = link ? `${window.location.origin}/a/${link.public_token}` : null;

  const copyLink = () => {
    if (!linkUrl) return;
    navigator.clipboard.writeText(linkUrl)
      .then(() => toast.success('Copied!'))
      .catch(() => toast.error('Failed to copy'));
  };

  return (
    <div className="space-y-6 text-center">
      {/* Success header */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-600">
          <ClipboardDocumentCheckIcon className="h-8 w-8" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 font-heading">Assessment Created!</h3>
          <p className="text-sm text-neutral-500 mt-1">
            <span className="font-medium text-neutral-700">{assessment.title}</span> is ready.
          </p>
        </div>
      </div>

      {/* Action cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
        {/* Publish */}
        <div className="rounded-card border border-neutral-200 p-5 space-y-3">
          <h4 className="text-sm font-semibold text-neutral-800">Publish Assessment</h4>
          <p className="text-xs text-neutral-500">
            Make this assessment live so participants can start submitting responses.
          </p>
          {published ? (
            <Badge variant="success">Published</Badge>
          ) : (
            <button
              type="button"
              className="btn-primary text-sm w-full"
              onClick={handlePublish}
              disabled={publishing}
            >
              {publishing && <Spinner size="sm" />}
              Publish Now
            </button>
          )}
        </div>

        {/* Share link */}
        <div className="rounded-card border border-neutral-200 p-5 space-y-3">
          <h4 className="text-sm font-semibold text-neutral-800">Create Share Link</h4>
          <p className="text-xs text-neutral-500">
            Generate a unique link to share with participants.
          </p>
          {link ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
                <span className="flex-1 text-xs font-mono text-neutral-700 truncate">{linkUrl}</span>
                <button
                  type="button"
                  onClick={copyLink}
                  className="shrink-0 text-neutral-400 hover:text-primary-600 transition-colors"
                  title="Copy"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
              <button type="button" className="btn-ghost text-xs w-full" onClick={handleCreateLink} disabled={creatingLink}>
                {creatingLink && <Spinner size="sm" />}
                Create another link
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary text-sm w-full"
              onClick={handleCreateLink}
              disabled={creatingLink}
            >
              {creatingLink && <Spinner size="sm" />}
              Create Share Link
            </button>
          )}
        </div>
      </div>

      {/* Navigate */}
      <div className="flex justify-center pt-2">
        <Link to={`/assessments/${assessment.id}`} className="btn-primary text-sm">
          View Assessment →
        </Link>
      </div>
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────────────── */
export default function CreateAssessmentPage() {
  const [step, setStep] = useState(1);
  const [assessment, setAssessment] = useState(null);

  const handleCreated = (a) => {
    setAssessment(a);
    setStep(2);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-8">
      <div>
        <Link
          to="/assessments"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-3"
        >
          ← Back to Assessments
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 font-heading">Create Assessment</h1>
        <p className="text-sm text-neutral-500 mt-0.5">Follow the steps below to set up your assessment.</p>
      </div>

      <StepIndicator current={step} />

      <Card>
        {step === 1 && <Step1Details onCreated={handleCreated} />}
        {step === 2 && assessment && (
          <Step2Questions
            assessment={assessment}
            onContinue={() => setStep(3)}
            onSkip={() => setStep(3)}
          />
        )}
        {step === 3 && assessment && <Step3Share assessment={assessment} />}
      </Card>
    </div>
  );
}
